import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CrmPushJob from "@/model/interface/CrmPushJob";
import { useGetPushJobsQuery, useGetPushJobSummaryQuery, useRetryPushJobMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  service: string;
}

export default function IntegrationPushQueue({ service }: Props) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(0);

  const { data: summary } = useGetPushJobSummaryQuery({ service });
  const { data: jobs, isLoading } = useGetPushJobsQuery({
    service,
    status: statusFilter === "all" ? undefined : statusFilter,
    page,
    size: 25,
  });
  const [retryPushJob] = useRetryPushJobMutation();

  const handleRetry = async (id: string) => {
    try {
      await retryPushJob(id).unwrap();
      toast.success("Push job requeued");
    } catch {
      toast.error("Retry failed");
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "DONE":
        return <Badge variant="default">Done</Badge>;
      case "FAILED":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summary.pending}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-destructive">{summary.failed}</div>
              <p className="text-xs text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summary.done}</div>
              <p className="text-xs text-muted-foreground">Done</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Push Queue</CardTitle>
              <CardDescription>Contacts queued to push into this CRM, paced and rate-limit aware</CardDescription>
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="DONE">Done</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Next Attempt</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Error</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {jobs?.items.map((job: CrmPushJob) => (
                <TableRow key={job.id}>
                  <TableCell>{statusBadge(job.status)}</TableCell>
                  <TableCell className="text-xs font-mono truncate max-w-[120px]">{job.contactId}</TableCell>
                  <TableCell>{job.attempts}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {job.status === "PENDING" ? new Date(job.nextAttemptAt).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{job.source || "—"}</TableCell>
                  <TableCell
                    className="text-xs text-destructive truncate max-w-[200px]"
                    title={job.lastError ?? undefined}
                  >
                    {job.lastError || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {job.status === "FAILED" && (
                      <Button variant="outline" size="sm" onClick={() => handleRetry(job.id)}>
                        Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && (!jobs?.items || jobs.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No push jobs
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {jobs && jobs.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-sm self-center text-muted-foreground">
                Page {page + 1} of {jobs.totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={jobs.last} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
